import {AfterViewInit, Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormControl} from '@angular/forms';
import {MatPaginator, MatSelectChange, MatSort, Sort} from '@angular/material';
import {ReplaySubject, Subject} from 'rxjs';
import {takeUntil, tap} from 'rxjs/internal/operators';
import {Title} from '@angular/platform-browser';

import swal from 'sweetalert2';
import * as moment from 'moment';
import {fuseAnimations} from '@fuse/animations';

import {BaseService} from '../../../services/base.service';
import {AuthService} from '../../../services/auth.service';
import {BaseDatasourceService} from '../../../services/base-datasource.service';
import {Endpoints} from '../../../config/endpoints';
import {SERVER_DATE_FORMAT_WITH_TIME} from '../../../config/date_format';
import {User} from '../../../models/user';
import {MarketLevel} from '../../../models/market-level';
import {Market} from '../../../models/market';
import {UserRole} from '../../../models/user-role';
import {Attendance} from '../../../models/attendance';
import {getEnumSelector} from '../../../services/utilities';
import {ForwardStatusEnum} from '../../../enums/forward-status-enum';
import {AttendanceTypeEnum} from '../../../enums/attendance-type-enum';
import {TrackingLocation} from '../../../models/tracking-location';
import {SyncStatusEnum} from '../../../enums/sync-status-enum';
import {TitleBroadcastService} from '../../../services/title-broadcast.service';

@Component({
    selector: 'attendance-list',
    templateUrl: './attendance-list.component.html',
    styleUrls: ['./attendance-list.component.scss'],
    animations: fuseAnimations
})
export class AttendanceListComponent implements OnInit, AfterViewInit {

    title = 'Attendance List';

    loggedUser: User;
    canForwardAttendance = false;
    canApproveAttendance = false;
    loadingData = false;
    lat = 23.8383283;
    lng = 90.3759853;

    marketLevels: MarketLevel[] = [];
    markets = [];
    selectedMarket: Market[] = [];
    locationList: TrackingLocation[] = [];

    userList: User[] = [];
    filteredUserList: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
    userFilter: FormControl = new FormControl();
    userType = 'ORGANIZATION_USER';
    userRoles: UserRole[] = [];
    attendanceList = [];
    hasAutoApprovePermission = false;

    dataSource: BaseDatasourceService<Attendance>;
    displayedColumns: string[] = ['Name', 'Shift', 'CreatedAt', 'AttendanceType', 'SelfieImage'];
    filters = {
        'OrganizationId': null,
        'FromDateTime': moment().set({hour: 0, minute: 0, second: 0}).format(SERVER_DATE_FORMAT_WITH_TIME),
        'ToDateTime': moment().set({hour: 23, minute: 59, second: 59}).format(SERVER_DATE_FORMAT_WITH_TIME),
        'MarketIdList': null,
        'Only': null,
        'UserId': null,
        'ShowAllUsers': true,
        'UserRoleId': null,
        'ApproveFilter': null,
        'AttendanceType': null,
        'SyncStatus': null,
        'SearchKey': null,
        'SortBy': 'CreatedAt',
        'SortOrder': 'asc',
        'PageNumber': 0,
        'RecordsPerPage': 20
    };

    statusList = getEnumSelector(ForwardStatusEnum);
    statusEnum = ForwardStatusEnum;
    attendanceTypes = getEnumSelector(AttendanceTypeEnum);
    syncStatus = getEnumSelector(SyncStatusEnum);

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    private _unsubscribeAll: Subject<any>;

    // tslint:disable-next-line:typedef
    @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.reloadDatatable(true);
        }
    }


    constructor(private route: ActivatedRoute, private service: BaseService, private router: Router, private auth: AuthService,
                private titleService: Title, private titleBroadcastService: TitleBroadcastService) {
        this._unsubscribeAll = new Subject();
        this.titleBroadcastService.title.next('Attendance List');
        this.loggedUser = auth.user;
        this.canForwardAttendance = this.auth.hasAccess('USER_ATTENDANCE', 'FORWARD_USER_ATTENDANCE');
        this.canApproveAttendance = this.auth.hasAccess('USER_ATTENDANCE', 'APPROVE_ATTENDANCE');

        this.marketLevels = this.loggedUser.MarketLevelHierarchy;
        this.marketLevels.forEach(pMarketLevel => {
            this.markets[pMarketLevel.Rank] = [];
            this.selectedMarket[pMarketLevel.Rank] = null;
        });
    }

    ngOnInit(): void {

        this.titleService.setTitle(this.title);

        this.service.getOrganizationSettings(this.loggedUser.Organization.Id).subscribe(pOrganizationSettings => {
            let autoApproveUserAttendance = false;
            const hasDistanceSettings = [];
            pOrganizationSettings.forEach(pModule => {
                if (pModule.ModuleName === 'USER_ATTENDANCE') {
                    pModule.ModularSettings.forEach(pSettings => {
                        if (pSettings.SettingsName === 'AUTO_APPROVE_USER_ATTENDANCE') {
                            autoApproveUserAttendance = pSettings.SettingsValue === 'true';
                        }
                        if (pSettings.SettingsName === 'CALCULATE_DISTANCE_BY_TOUR_PLAN') {
                            if (pSettings.SettingsValue === 'true') {
                                hasDistanceSettings.push(pSettings.SettingsValue);
                            }
                        }
                        if (pSettings.SettingsName === 'CALCULATE_DISTANCE_FORM_DISTRIBUTOR') {
                            if (pSettings.SettingsValue === 'true') {
                                hasDistanceSettings.push(pSettings.SettingsValue);
                            }
                        }
                        if (pSettings.SettingsName === 'CALCULATE_DISTANCE_FORM_RETAILER') {
                            if (pSettings.SettingsValue === 'true') {
                                hasDistanceSettings.push(pSettings.SettingsValue);
                            }
                        }
                        if (pSettings.SettingsName === 'CALCULATE_DISTANCE_FORM_PROJECT') {
                            if (pSettings.SettingsValue === 'true') {
                                hasDistanceSettings.push(pSettings.SettingsValue);
                            }
                        }
                        if (pSettings.SettingsName === 'CALCULATE_DISTANCE_FROM_ATTENDANCE_POINT') {
                            if (pSettings.SettingsValue === 'true') {
                                hasDistanceSettings.push(pSettings.SettingsValue);
                            }
                        }
                    });
                }
            });
            if (hasDistanceSettings.length > 0) {
                this.displayedColumns.push('Distance');
            }
            this.displayedColumns.push('Location', 'Comment', 'Status', 'ApprovedAt', 'SyncStatus');
            if (autoApproveUserAttendance) {
                this.displayedColumns.push('SystemNote');
            }
            this.displayedColumns.push('star');
        });


        this.getUserList();
        this.userFilter.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            if (!this.userList) {
                return;
            }

            const search = this.userFilter.value;
            if (!search) {
                this.filteredUserList.next(this.userList.slice());
                return;
            }
            this.getUserList(search);
            this.filteredUserList.next(this.userList.filter(user => user.UserName.toLowerCase().indexOf(search.toLowerCase()) > -1 || user.UserCode.toLowerCase().indexOf(search.toLowerCase()) > -1));
        });

        this.service.getUserRoleList({Active: true, UserType: this.userType}).subscribe(pUserRoles => this.userRoles = pUserRoles);

        this.dataSource = new BaseDatasourceService<Attendance>(this.service);

        this.reloadDatatable();
    }

    ngAfterViewInit(): void {
        this.service.getMarketList({MarketLevelId: this.marketLevels[0].Id, Active: true}).subscribe(pMarkets => this.markets[1] = pMarkets);
        this.paginator.page.pipe(tap(() => this.reloadDatatable())).subscribe();
    }

    getUserList(search?: string): void {
        let marketIdList = null;
        for (let i = this.selectedMarket.length - 1; i >= 0; i--) {
            if (this.selectedMarket[i]) {
                marketIdList = [this.selectedMarket[i].Id];
                break;
            }
        }

        this.service.getUserList({
            ResponseSize: 'MINIFY',
            Active: true,
            PageNumber: 0,
            RecordsPerPage: 50,
            SearchKey: search,
            UserType: this.userType,
            MarketIdList: marketIdList,
            UserRoleId: this.filters.UserRoleId,
        }).subscribe(pUsers => {
            this.userList = pUsers;
            this.filteredUserList.next(this.userList.slice());
        });
    }

    roleChangeListener(pEvent: MatSelectChange): void {
        this.getUserList();
    }

    _resetChildrenDropdown(pParentRank: number): void {
        this.marketLevels.forEach(pMarketLevel => {
            if (pMarketLevel.Rank > pParentRank) {
                this.selectedMarket[pMarketLevel.Rank] = null;
                this.markets[pMarketLevel.Rank] = [];
            }
        });
    }

    populateChildrenMarkets(event: MatSelectChange): void {
        const market: Market = event.value;
        if (market) {
            this._resetChildrenDropdown(market.MarketRank);
            if (this.marketLevels[market.MarketRank]) {
                this.service.getMarketList({
                    MarketLevelId: this.marketLevels[market.MarketRank].Id,
                    ParentMarketId: market.Id,
                    Active: true
                }).subscribe(pMarkets => this.markets[market.MarketRank + 1] = pMarkets);
            }
        } else {
            this._resetChildrenDropdown(parseInt(event.source.id.slice(6), 10));
        }

        this.getUserList();
    }

    setFromDate(pEvent): void {
        const eventDate = moment(pEvent.value);
        eventDate.set('hour', 0);
        eventDate.set('minute', 0);
        eventDate.set('second', 0);
        this.filters.FromDateTime = moment(eventDate).format(SERVER_DATE_FORMAT_WITH_TIME);
    }

    setToDate(pEvent): void {
        const eventDate = moment(pEvent.value);
        eventDate.set('hour', 23);
        eventDate.set('minute', 59);
        eventDate.set('second', 59);
        this.filters.ToDateTime = moment(eventDate).format(SERVER_DATE_FORMAT_WITH_TIME);
    }

    showImage(pAttendance: Attendance): void {
        swal({
            title: pAttendance.CreatedBy.UserName + ' (' + pAttendance.CreatedBy.UserCode + ') at ' + pAttendance.CreatedAt,
            imageUrl: pAttendance.SelfieImage || 'assets/images/no_image.jpg',
            position: 'center',
            showCloseButton: true,
            showConfirmButton: false,
            showCancelButton: false
        });
    }

    checkApprovedBy(approvedBy, forwardedBy): any {
        if (approvedBy != null) {
            return approvedBy;
        }
        if (approvedBy == null && forwardedBy != null) {
            return forwardedBy;
        }
        if (approvedBy == null && forwardedBy == null) {
            return;
        }
    }

    checkApproveTime(approvedAt, forwardedAt): any {
        if (approvedAt != null) {
            return approvedAt;
        }
        if (approvedAt == null && forwardedAt != null) {
            return forwardedAt;
        }
        if (approvedAt == null && forwardedAt == null) {
            return;
        }
    }

    reloadDatatable(pSearch?: boolean): void {
        this.filters.MarketIdList = null;
        for (let i = this.selectedMarket.length - 1; i >= 0; i--) {
            if (this.selectedMarket[i]) {
                this.filters.MarketIdList = [this.selectedMarket[i].Id];
                break;
            }
        }


        this.filters.PageNumber = pSearch === true ? 0 : this.paginator.pageIndex || 0;
        this.filters.RecordsPerPage = this.paginator.pageSize || 20;
        if (pSearch === true) {
            this.paginator.firstPage();
        }

        this.dataSource.fetchRemoteData(Endpoints.attendance_list, this.filters);
    }

    statusChange(pAttendance: Attendance, pStatus: string): void {
        swal({
            title: 'Are you sure?',
            text: '',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085D6',
            cancelButtonColor: '#DD3333',
            confirmButtonText: 'Yes, ' + (pStatus === this.statusEnum.FORWARDED ? 'forward' : pStatus === this.statusEnum.APPROVED ? 'approve' : 'reject') + ' it!'
        }).then((result) => {
            if (result.value) {
                if (pStatus === this.statusEnum.FORWARDED) {
                    pAttendance.Forwarded = true;
                } else if (pStatus === this.statusEnum.APPROVED) {
                    pAttendance.IsApproved = true;
                } else if (pStatus === this.statusEnum.REJECTED) {
                    pAttendance.Forwarded = false;
                    pAttendance.IsApproved = false;
                }

                this.service.saveAttendance(pAttendance).subscribe(() => {
                    swal(pStatus + '!', 'The attendance has been ' + pStatus.toLowerCase() + '.', 'success');
                    this.reloadDatatable();
                });
            }
        });
    }
    sortChange(sortState: Sort): void {
        if (sortState.direction) {
            this.filters.SortBy = sortState.active;
            this.filters.SortOrder = sortState.direction;
        } else {
            this.filters.SortBy = 'Default';
            this.filters.SortOrder = sortState.direction;
        }

        this.dataSource.fetchRemoteData(Endpoints.attendance_list, this.filters);


    }
}
